/**
 * FxTriggers — small DOM-based effect helpers (0 KB, CSS-driven).
 *
 * Complements FxCanvas (particles) with lightweight DOM effects:
 *  - floating text (damage numbers, +gold, ATK changes) anchored to an
 *    element, animated by CSS keyframes, self-removing on animationend
 *  - full-screen flash (elimination / victory moments)
 *  - re-triggerable CSS animation classes (card slam, draw, level-up, …)
 *
 * Everything here is fire-and-forget and suppressed when the user prefers
 * reduced motion (and by the CSS media query as a second layer).
 */

let _reducedMotionCache: boolean | null = null;

/** Whether the user prefers reduced motion (cached, guarded). */
export function isReducedMotion(): boolean {
  if (_reducedMotionCache === null) {
    try {
      _reducedMotionCache =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
          : false;
    } catch {
      _reducedMotionCache = false;
    }
  }
  return _reducedMotionCache;
}

export type FloatVariant = 'dmg' | 'gold' | 'atk' | 'heal';

/**
 * Font size for magnitude-scaled float text: the bigger the number, the
 * bigger the indicator (1 → ~19px, 10 → ~28px, capped at ~43px for 24+).
 */
function magnitudeFontSize(amount: number): number {
  const clamped = Math.max(1, Math.min(amount, 24));
  return 18 + clamped * 1.05;
}

/**
 * Shows floating text above a DOM element (e.g. "-3" damage, "+2" gold).
 * The element is removed automatically (animationend + safety timeout).
 * No-op for null elements or reduced motion.
 *
 * @param amount Optional magnitude (damage/heal amount). When provided, the
 *   font size scales with the number so big hits read as big hits.
 */
export function floatText(
  el: Element | null | undefined,
  text: string,
  variant: FloatVariant = 'dmg',
  amount?: number
): void {
  if (!el || isReducedMotion()) return;
  try {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    const node = document.createElement('div');
    node.className = `fx-float-text fx-float-${variant}`;
    node.textContent = text;
    node.style.left = `${rect.left + rect.width / 2}px`;
    node.style.top = `${Math.max(rect.top - 6, 4)}px`;
    if (amount !== undefined && amount > 0) {
      node.style.fontSize = `${magnitudeFontSize(amount)}px`;
    }
    document.body.appendChild(node);

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      node.remove();
    };
    node.addEventListener('animationend', remove, { once: true });
    // Safety: if the animation never ends (reduced-motion CSS override,
    // tab hidden, …) remove it anyway.
    setTimeout(remove, 2500);
  } catch {
    /* effects must never break the game */
  }
}

/**
 * "BOOM!" — the Explosive/Exploding signature text: large, growing rapidly,
 * fading out while wobbling (the CSS keyframes live in index.html,
 * `.fx-boom-text` / `@keyframes fxBoom`). Anchored at the element's center
 * (the minion that exploded). Self-removing on animationend + safety
 * timeout; no-op for null/zero-size elements or reduced motion.
 */
export function boomText(el: Element | null | undefined): void {
  if (!el || isReducedMotion()) return;
  try {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    const node = document.createElement('div');
    node.className = 'fx-boom-text';
    node.textContent = 'BOOM!';
    node.style.left = `${rect.left + rect.width / 2}px`;
    node.style.top = `${rect.top + rect.height / 2}px`;
    document.body.appendChild(node);

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      node.remove();
    };
    node.addEventListener('animationend', remove, { once: true });
    // Safety: if the animation never ends (reduced-motion CSS override,
    // tab hidden, …) remove it anyway.
    setTimeout(remove, 1500);
  } catch {
    /* effects must never break the game */
  }
}

export type FlashVariant = 'red' | 'gold' | 'green';

/**
 * Full-screen color flash (red = bad moment, gold = good moment,
 * green = the LifeBurst board-wide heal — task 206). Self-removing;
 * no-op under reduced motion.
 */
export function screenFlash(variant: FlashVariant): void {
  if (isReducedMotion()) return;
  try {
    const node = document.createElement('div');
    node.className = `fx-screen-flash fx-screen-flash-${variant}`;
    document.body.appendChild(node);
    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      node.remove();
    };
    node.addEventListener('animationend', remove, { once: true });
    setTimeout(remove, 2000);
  } catch {
    /* no-op */
  }
}

/**
 * (Re)starts a CSS animation on an element by toggling a class.
 * Used for effects that retrigger on the same node (gold pop, life hurt,
 * level-up ring, card slam, …). No-op under reduced motion.
 * @returns true when the animation class was applied.
 */
export function retriggerClass(el: HTMLElement | null | undefined, cls: string): boolean {
  if (!el || isReducedMotion()) return false;
  try {
    el.classList.remove(cls);
    void el.offsetWidth; // force reflow so the animation restarts
    el.classList.add(cls);
    return true;
  } catch {
    return false;
  }
}
